import Phaser from 'phaser';
import { audio } from '../game/AudioManager.js';
import { questManager } from '../game/QuestManager.js';
import { gameState } from '../game/GameState.js';
import { DesktopSimulation } from '../computer/DesktopSimulation.js';
import { promptManager } from '../game/UIManager.js';
import { TouchControls } from '../game/TouchControls.js';

export class Level2Scene extends Phaser.Scene {
  constructor() {
    super('Level2Scene');
    this.player = null;
    this.computerZone = null;
    this.promptText = null;
    this.desktopSim = null;
    this.desktopActive = false;
    this.touchControls = null;
    this.touchMoveX = 0;
  }

  create() {
    gameState.currentChapter = 2;
    questManager.show(true);
    const isTouch = (
      'ontouchstart' in window ||
      navigator.maxTouchPoints > 0 ||
      window.matchMedia('(pointer: coarse)').matches
    );
    const keyHint = isTouch ? '[A]' : '[E]';
    questManager.setQuest("The Bedroom Desk", `Walk to the desk and press ${keyHint} to use computer`, "CHAPTER 2 • 2016");
    audio.playTheme('bedroom');

    const { width, height } = this.scale;

    // Authentic 2016 Pixel-art Bedroom Background
    this.add.image(0, 0, 'bg_talha_bedroom')
      .setOrigin(0, 0)
      .setDisplaySize(width, height)
      .setDepth(0);

    // Warm bedroom ambient lighting & monitor glow
    const ambientLight = this.add.graphics();
    ambientLight.fillGradientStyle(0x38bdf8, 0x1e1b4b, 0x0f172a, 0x000000, 0.15);
    ambientLight.fillRect(0, 0, width, height);
    ambientLight.setDepth(1);

    // Subtle pulsing monitor blue glow directly over the computer monitor screen (x: 211, y: 279)
    const monitorGlow = this.add.ellipse(211, 279, 130, 80, 0x38bdf8, 0.25);
    monitorGlow.setDepth(2);
    this.tweens.add({
      targets: monitorGlow,
      alpha: 0.35,
      duration: 1500,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });

    // Player Talha (2016 adult) starts on right side near bed, facing left toward desk
    this.player = this.physics.add.sprite(560, height - 90, 'talha_sheet')
      .setScale(0.38)
      .setOrigin(0.5, 0.95)
      .setDepth(10)
      .setFlipX(true)
      .play('talha_idle');
    this.player.setCollideWorldBounds(true);
    this.player.body.setSize(60, 36).setOffset(98, 208);

    // Keys
    this.cursors = this.input.keyboard.createCursorKeys();
    this.wasd = this.input.keyboard.addKeys({
      left: Phaser.Input.Keyboard.KeyCodes.A,
      right: Phaser.Input.Keyboard.KeyCodes.D,
      interact: Phaser.Input.Keyboard.KeyCodes.E,
      jump: Phaser.Input.Keyboard.KeyCodes.SPACE
    });

    // Mount Universal Touch Controls (Adventure mode)
    this.touchControls = new TouchControls(this, 'adventure', {
      onMove: (x, y) => {
        this.touchMoveX = x;
        this.touchMoveY = y;
      },
      onJump: () => {
        this.handleJump();
      },
      onInteract: () => {
        this.handleInteract();
      }
    });
    this.touchControls.mount();

    this.events.on('shutdown', () => {
      if (this.touchControls) {
        this.touchControls.destroy();
        this.touchControls = null;
      }
    });
  }

  handleJump() {
    if (this.desktopActive || !this.player || !this.player.body || this.isJumping) return;
    this.isJumping = true;
    audio.playClick();
    this.player.play('talha_jump');
    const startY = this.player.y;
    this.tweens.add({
      targets: this.player,
      y: startY - 45,
      duration: 220,
      yoyo: true,
      ease: 'Power1',
      onComplete: () => {
        this.isJumping = false;
        if (this.player && this.player.active) {
          this.player.y = startY;
        }
      }
    });
  }

  handleInteract() {
    if (this.desktopActive || !this.player) return;
    const isNearComputer = Math.abs(this.player.x - 215) < 100;
    if (isNearComputer) {
      promptManager.hide();
      if (this.touchControls) this.touchControls.setInteractHighlight(false);
      this.openComputerUI();
    }
  }

  update() {
    if (this.desktopActive || !this.player) return;

    // Pause player movement while dialogue box is open
    const dlg = document.getElementById('dialogue');
    if (dlg && !dlg.classList.contains('hidden')) {
      this.player.setVelocityX(0);
      if (this.player.anims && this.player.anims.currentAnim?.key !== 'talha_idle') {
        this.player.play('talha_idle');
      }
      return;
    }

    const speed = 180;
    let vx = 0;

    const wantsJump = Phaser.Input.Keyboard.JustDown(this.wasd.jump) ||
      Phaser.Input.Keyboard.JustDown(this.cursors.up);

    if (wantsJump) {
      this.handleJump();
    }

    const keyLeft = this.cursors.left.isDown || this.wasd.left.isDown;
    const keyRight = this.cursors.right.isDown || this.wasd.right.isDown;
    const touchX = this.touchMoveX || 0;
    const wantsLeft = keyLeft || touchX < -0.15;
    const wantsRight = keyRight || touchX > 0.15;

    if (!this.isJumping) {
      if (wantsLeft) {
        const speedScale = keyLeft ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
        vx = -speed * speedScale;
        this.player.setFlipX(true);
        if (this.player.anims.currentAnim?.key !== 'talha_walk') this.player.play('talha_walk');
      } else if (wantsRight) {
        const speedScale = keyRight ? 1 : Math.min(1, Math.max(0.45, Math.abs(touchX)));
        vx = speed * speedScale;
        this.player.setFlipX(false);
        if (this.player.anims.currentAnim?.key !== 'talha_walk') this.player.play('talha_walk');
      } else {
        if (this.player.anims.currentAnim?.key !== 'talha_idle') this.player.play('talha_idle');
      }
    } else {
      if (wantsLeft) {
        vx = -speed * 1.1;
        this.player.setFlipX(true);
      } else if (wantsRight) {
        vx = speed * 1.1;
        this.player.setFlipX(false);
      }
    }

    this.player.setVelocityX(vx);

    const isNearComputer = Math.abs(this.player.x - 215) < 100;
    if (isNearComputer) {
      promptManager.show('E', 'Use Computer');
      if (this.touchControls) this.touchControls.setInteractHighlight(true, 'Use PC');
      if (Phaser.Input.Keyboard.JustDown(this.wasd.interact)) {
        this.handleInteract();
      }
    } else {
      promptManager.hide();
      if (this.touchControls) this.touchControls.setInteractHighlight(false, 'Use PC');
    }
  }

  openComputerUI() {
    this.desktopActive = true;
    promptManager.hide();
    if (this.touchControls) this.touchControls.show(false);
    this.player.setVelocity(0, 0);
    this.player.play('talha_idle');
    audio.playRomanticChime();

    const appRoot = document.getElementById('game-shell') || document.getElementById('app');
    this.desktopSim = new DesktopSimulation(appRoot, () => {
      this.desktopSim.destroy();
      this.scene.start('MontageScene');
    });
    this.desktopSim.mount();
  }
}
